// #85 - the agency's principal logins (role "agency" users pointed at this
// agency), shown to Applied GA staff inside PURL Agency Info. Answers "who runs
// this agency and under what email" at a glance, flags principals with no linked
// agent record, and gives admin staff two password actions: email a reset link
// (admin + Lite Admin), or set a password directly (full admin only; the
// backend's role guards enforce the same split).
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useDataProvider, useGetList, useNotify } from "react-admin";
import { useAuth } from "../../../hooks/useAuth";
import UserService from "../../../services/user.service";

const fullName = (p) =>
  [p.firstName, p.lastName].filter(Boolean).join(" ").trim() || "(no name)";

const SetPasswordDialog = ({ principal, onClose }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const tooShort = password.length > 0 && password.length < 8;

  const save = async () => {
    setBusy(true);
    try {
      await dataProvider.update("user", {
        id: principal.id,
        data: { password },
        previousData: principal,
      });
      notify(`Password set for ${principal.email}`, { type: "success" });
      onClose();
    } catch (e) {
      notify("Could not set the password", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Set password for {fullName(principal)}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          type="password"
          label="New password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={tooShort}
          helperText={
            tooShort
              ? "Use at least 8 characters"
              : `The principal signs in as ${principal.email} with this password.`
          }
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={busy || password.length < 8}
        >
          {busy ? "Saving..." : "Set password"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Principals = ({ agencyId }) => {
  const { user, isFullAdmin, isLiteAdmin } = useAuth();
  const notify = useNotify();
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const { data: principals, isLoading } = useGetList("user", {
    filter: { agency: agencyId, role: "agency" },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "lastName", order: "ASC" },
  });

  const canReset = isFullAdmin(user) || isLiteAdmin(user);
  const canSetPassword = isFullAdmin(user);

  const sendReset = async (principal) => {
    setBusyId(principal.id);
    try {
      const { message } = await UserService.sendReset(principal.id);
      notify(message || `Reset link emailed to ${principal.email}`, {
        type: "success",
      });
    } catch (e) {
      notify("Could not send the reset link", { type: "error" });
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading || !agencyId) return null;

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Principals (agency admin logins)
      </Typography>
      {!principals?.length ? (
        <Alert severity="warning">
          No principal login for this agency yet. Create one under Users with
          the role "agency" and this agency selected.
        </Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Login email</TableCell>
              <TableCell>Agent record</TableCell>
              {(canReset || canSetPassword) && (
                <TableCell align="right">Password</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {principals.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{fullName(p)}</TableCell>
                <TableCell>{p.email}</TableCell>
                <TableCell>
                  {p.agent ? (
                    <Chip size="small" color="success" variant="outlined" label="Linked" />
                  ) : (
                    <Chip size="small" variant="outlined" label="Not linked" />
                  )}
                </TableCell>
                {(canReset || canSetPassword) && (
                  <TableCell align="right">
                    {canReset && (
                      <Button
                        size="small"
                        disabled={busyId === p.id}
                        onClick={() => sendReset(p)}
                      >
                        {busyId === p.id ? "Sending..." : "Email reset link"}
                      </Button>
                    )}
                    {canSetPassword && (
                      <Button size="small" onClick={() => setPasswordTarget(p)}>
                        Set password
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {passwordTarget ? (
        <SetPasswordDialog
          principal={passwordTarget}
          onClose={() => setPasswordTarget(null)}
        />
      ) : null}
    </Box>
  );
};

export default Principals;
